import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import parameters from '@atlassian/jira-relay/src/__generated__/srcIncidentsPageInternalQuery$parameters';
import type {
	GraphStoreIncidentFilter,
	IncidentStatusEnums,
	ParsedQueryParams,
	Status,
	SearchQueryParams,
	GraphStoreIncidentSort,
} from '@atlassian/jira-software-incidents-query-param-controller/src/common/types.tsx';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';

const AriGraphSortOrderInput = {
	ASC: 'ASC',
	DESC: 'DESC',
} as const;
type AriGraphSortOrderInputType =
	(typeof AriGraphSortOrderInput)[keyof typeof AriGraphSortOrderInput];

const QUERY_PARAM_SEARCH = 'search' as const;
const QUERY_PARAM_PRIORITY = 'priority' as const;
const QUERY_PARAM_AFFECTED_SERVICE = 'affected-service' as const;
const QUERY_PARAM_STATUS = 'status' as const;
const QUERY_PARAM_SORT_BY = 'sortBy' as const;
const QUERY_PARAM_SORT_ORDER = 'sortOrder' as const;

const STATUS_SORT_KEY = 'status' as const;
const CREATED_SORT_KEY = 'created' as const;
const PRIORITY_SORT_KEY = 'priority' as const;
const SUMMARY_SORT_KEY = 'key' as const;

const ASC_SORT_KEY = 'ASC' as const;
const DESC_SORT_KEY = 'DESC' as const;

type SortOrder = typeof ASC_SORT_KEY | typeof DESC_SORT_KEY;
type SortBy =
	| typeof STATUS_SORT_KEY
	| typeof CREATED_SORT_KEY
	| typeof PRIORITY_SORT_KEY
	| typeof SUMMARY_SORT_KEY;

function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
	return Boolean(value);
}

const isPriority = notEmpty;

const isStatus = (val: string): val is Status => val === 'resolved' || val === 'open';

const isSortBy = (val: string): val is SortBy =>
	val === STATUS_SORT_KEY ||
	val === CREATED_SORT_KEY ||
	val === PRIORITY_SORT_KEY ||
	val === SUMMARY_SORT_KEY;

const isSortOrder = (val: string): val is SortOrder =>
	val === ASC_SORT_KEY || val === DESC_SORT_KEY;

const isAffectedService = notEmpty;

function parseParameter<T extends string>(
	parameter: string | undefined,
	parameterValidationFn: (value: string) => value is T,
): T[] | undefined {
	return parameter == null
		? undefined
		: parameter
				.split(',')
				.map((str) => str.trim())
				.filter(parameterValidationFn);
}
const parseQueryParams = (paramsRaw: SearchQueryParams): ParsedQueryParams => {
	const result: ParsedQueryParams = {
		[QUERY_PARAM_SORT_BY]:
			paramsRaw[QUERY_PARAM_SORT_BY] && isSortBy(paramsRaw[QUERY_PARAM_SORT_BY])
				? paramsRaw[QUERY_PARAM_SORT_BY]
				: CREATED_SORT_KEY,
		[QUERY_PARAM_SORT_ORDER]:
			paramsRaw[QUERY_PARAM_SORT_ORDER] && isSortOrder(paramsRaw[QUERY_PARAM_SORT_ORDER])
				? paramsRaw[QUERY_PARAM_SORT_ORDER]
				: DESC_SORT_KEY,
	};
	result[QUERY_PARAM_AFFECTED_SERVICE] = parseParameter(
		paramsRaw[QUERY_PARAM_AFFECTED_SERVICE],
		isAffectedService,
	);
	result[QUERY_PARAM_STATUS] = parseParameter(paramsRaw[QUERY_PARAM_STATUS], isStatus);
	result[QUERY_PARAM_SEARCH] = paramsRaw[QUERY_PARAM_SEARCH] ?? undefined;
	result[QUERY_PARAM_PRIORITY] = parseParameter(paramsRaw[QUERY_PARAM_PRIORITY], isPriority);

	return result;
};

const getFilterValue = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	values: any[],
) => ({
	...(values.length > 0 ? { is: values } : {}),
});
const parseStatusToFilterValues = (status: Status[]): IncidentStatusEnums[] =>
	status.flatMap((stat) => {
		switch (stat) {
			case 'open':
				return ['NEW', 'INDETERMINATE'];
			case 'resolved':
				return ['DONE'];
			default:
				return ['UNDEFINED'];
		}
	});

const getGqlFiltersFromQueryParams = (
	parsedQueryParams: ParsedQueryParams,
): GraphStoreIncidentFilter => ({
	and: [
		{
			to_status: getFilterValue(
				parseStatusToFilterValues(parsedQueryParams[QUERY_PARAM_STATUS] ?? []),
			),
			to_priority: getFilterValue(parsedQueryParams[QUERY_PARAM_PRIORITY] ?? []),
			to_affectedServiceAris: getFilterValue(parsedQueryParams[QUERY_PARAM_AFFECTED_SERVICE] ?? []),
		},
	],
});

const parseSortDirection = (
	maybeDirection: SortOrder,
	defaultValue: AriGraphSortOrderInputType,
): AriGraphSortOrderInputType => {
	switch (maybeDirection?.toUpperCase()) {
		case AriGraphSortOrderInput.ASC.toUpperCase():
			return AriGraphSortOrderInput.ASC;
		case AriGraphSortOrderInput.DESC.toUpperCase():
			return AriGraphSortOrderInput.DESC;
		default:
			return defaultValue;
	}
};
const getSortValue = (sortOrder: SortOrder) => ({
	priority: 2,
	direction: parseSortDirection(sortOrder, AriGraphSortOrderInput.DESC),
});

// Added Inverse sort fn as data in AGS is indexed in reverse order for priority
const parseInverseSortDirection = (
	maybeDirection: SortOrder,
	defaultValue: AriGraphSortOrderInputType,
): AriGraphSortOrderInputType => {
	switch (maybeDirection?.toUpperCase()) {
		case AriGraphSortOrderInput.ASC.toUpperCase():
			return AriGraphSortOrderInput.DESC;
		case AriGraphSortOrderInput.DESC.toUpperCase():
			return AriGraphSortOrderInput.ASC;
		default:
			return defaultValue;
	}
};
const getInverseSortValue = (sortOrder: SortOrder) => ({
	priority: 2,
	direction: parseInverseSortDirection(sortOrder, AriGraphSortOrderInput.ASC),
});

const getGraphStoreSortFromQueryParams = (
	parsedQueryParams: ParsedQueryParams,
): GraphStoreIncidentSort => ({
	lastModified: { priority: 1, direction: AriGraphSortOrderInput.DESC },
	...(STATUS_SORT_KEY === parsedQueryParams[QUERY_PARAM_SORT_BY]
		? { to_status: getSortValue(parsedQueryParams[QUERY_PARAM_SORT_ORDER]) }
		: {}),
	...(PRIORITY_SORT_KEY === parsedQueryParams[QUERY_PARAM_SORT_BY]
		? { to_priority: getInverseSortValue(parsedQueryParams[QUERY_PARAM_SORT_ORDER]) }
		: {}),
	...(CREATED_SORT_KEY === parsedQueryParams[QUERY_PARAM_SORT_BY]
		? { createdAt: getSortValue(parsedQueryParams[QUERY_PARAM_SORT_ORDER]) }
		: {}),
});

export const incidentsPageEntryPoint = createEntryPoint({
	root: JSResourceForUserVisible(() =>
		import(/* webpackChunkName: "async-incidents-page-3p" */ './src').then(
			(mod) => mod.IncidentsPageWrapper,
		),
	),
	getPreloadProps: ({ tenantContext: { cloudId }, context }: EntryPointRouteParams) => ({
		queries: {
			incidentPageData: {
				options: {
					fetchPolicy: 'store-or-network' as const,
				},
				parameters,
				variables: {
					incidentPageSize: 30,
					servicePageSize: 30,
					cloudId,
					projectKey: context.match.params.projectKey ?? '',
					filter: getGqlFiltersFromQueryParams(parseQueryParams(context.query)),
					sort: getGraphStoreSortFromQueryParams(parseQueryParams(context.query)),
					siteAri: `ari:cloud:jira::site/${cloudId}`,
				},
			},
		},
	}),
});
